'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import PropTypes from 'prop-types';
import Arrow from './Arrow';
import { useAuth } from '@/contexts/AuthContext';
import { pricePerTicketFor, resolveTierPrice, visibleTiers } from '@/lib/pricing';
import { savePendingEntry } from '@/lib/pendingEntry';
import { trackAddToCart } from '@/lib/tracking/events';
import { formatTime, price } from '@/lib/homepage/format';

const PRESETS = [1, 5, 10, 20];
const fmt = (n) => price(Math.round(n * 100) / 100);

function quote(c, qty) {
  const each = pricePerTicketFor({ basePrice: c.ticketPrice, bulkTiers: c.bulkPricingTiers, quantity: qty });
  const base = Number(c.ticketPrice || 0);
  return { each, total: each * qty, saved: Math.max(0, (base - each) * qty) };
}

/**
 * "Add 3 more for just £3.97 and all 10 drop to £1.79 each", or null. Only
 * offered when the next discount is close (at most half the way and 10
 * tickets), so it helps rather than pushes.
 */
export function nextTierNudge(c, qty) {
  const next = visibleTiers(c.bulkPricingTiers || []).find((t) => t.minQuantity > qty);
  if (!next) return null;
  const add = next.minQuantity - qty;
  if (add > 10 || add > Math.ceil(next.minQuantity / 2)) return null;
  const nextEach = resolveTierPrice(next, c.ticketPrice);
  const extra = next.minQuantity * nextEach - quote(c, qty).total;
  if (!(nextEach < Number(c.ticketPrice))) return null;
  return { add: next.minQuantity - qty, to: next.minQuantity, extra: Math.max(0, extra), each: nextEach };
}

function Entry({ c, qty, setQty, answer, setAnswer, error, compact }) {
  const q = quote(c, qty);
  const nudge = nextTierNudge(c, qty);
  const name = `q-${c.id}`;

  return (
    <div className={compact ? 'py-5 border-t border-white/10 first:border-t-0 first:pt-0' : ''}>
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="d [font-variation-settings:'wdth'_108] font-extrabold normal-case text-[1.2rem] leading-tight">
          {c.title}
        </h3>
        {compact && (
          <span className="shrink-0 text-sm font-semibold text-accent-bright tabular-nums">
            Closes {formatTime(c.endDate || c.drawDate)}
          </span>
        )}
      </div>

      <fieldset className="mt-4">
        <legend className="text-sm font-semibold">{c.question?.question}</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {(c.question?.options || []).map((o) => (
            <label
              key={o.value}
              className={`flex cursor-pointer items-center gap-3 rounded-[6px] border px-3 py-2.5 text-sm font-medium transition-colors ${
                answer === o.value ? 'border-accent bg-accent/10' : 'border-white/15 hover:border-white/35'
              }`}
            >
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={answer === o.value}
                onChange={() => setAnswer(o.value)}
                className="accent-[#32d800]"
              />
              {o.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-4">
        <p className="text-sm font-semibold">Lucky dip tickets</p>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {PRESETS.map((n) => {
            const pq = quote(c, n);
            return (
              <button
                key={n}
                type="button"
                onClick={() => setQty(n)}
                className={`rounded-[6px] border px-1 py-2 text-center leading-tight transition-colors ${
                  qty === n ? 'border-accent bg-accent/10 text-foreground' : 'border-white/15 text-foreground/85 hover:border-white/35'
                }`}
              >
                <span className="block font-bold">{n}</span>
                <span className="block text-sm tabular-nums">{fmt(pq.total)}</span>
                {pq.saved > 0.004 && (
                  <span className="block text-sm font-semibold text-accent-bright">save {fmt(pq.saved)}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            aria-label="One fewer ticket"
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="h-10 w-10 rounded-[6px] border border-white/15 text-lg font-bold hover:border-white/35"
          >
            −
          </button>
          <span className="min-w-[4.5rem] text-center font-semibold tabular-nums">
            {qty} ticket{qty === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            aria-label="One more ticket"
            onClick={() => setQty(Math.min(50, qty + 1))}
            className="h-10 w-10 rounded-[6px] border border-white/15 text-lg font-bold hover:border-white/35"
          >
            +
          </button>
          <span className="ml-auto text-right">
            <span className="num text-[1.6rem] text-accent-bright">{fmt(q.total)}</span>
            {q.saved > 0.004 && (
              <span className="block text-sm font-semibold text-foreground/70">You save {fmt(q.saved)}</span>
            )}
          </span>
        </div>
        {nudge && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-[6px] bg-accent/10 px-3 py-2 text-sm">
            <span>
              Add {nudge.add} more for just <strong>{fmt(nudge.extra)}</strong> and all {nudge.to} drop to{' '}
              <strong>{fmt(nudge.each)}</strong> each.
            </span>
            <button
              type="button"
              onClick={() => setQty(nudge.to)}
              className="shrink-0 rounded-[4px] bg-accent px-2.5 py-1.5 text-xs font-bold text-accent-foreground"
            >
              Add {nudge.add}
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-3 text-sm font-semibold text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

Entry.propTypes = {
  c: PropTypes.object.isRequired,
  qty: PropTypes.number.isRequired,
  setQty: PropTypes.func.isRequired,
  answer: PropTypes.string,
  setAnswer: PropTypes.func.isRequired,
  error: PropTypes.string,
  compact: PropTypes.bool,
};

/**
 * Enter from the homepage without leaving it: answer the question, choose a
 * number of lucky-dip tickets (with the real bundle prices), add to basket.
 * `competitions` has one competition, or several for "enter all tonight's
 * draws". `suggestions` are other competitions to offer once added.
 */
export default function QuickEntrySheet({ competitions, suggestions = [], title, onClose, onSwitch }) {
  const { isAuthenticated, loading } = useAuth();
  const [qty, setQty] = useState(() => Object.fromEntries(competitions.map((c) => [c.id, 1])));
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Competitions already in the basket from an earlier try: a retry must
  // not add their tickets again.
  const [addedSoFar, setAddedSoFar] = useState([]);
  const panelRef = useRef(null);
  const multi = competitions.length > 1;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Totals for what's still to add (not competitions already added on an
  // earlier try).
  const toAdd = useMemo(
    () => competitions.filter((c) => !addedSoFar.some((a) => a.c.id === c.id)),
    [competitions, addedSoFar]
  );
  const total = useMemo(
    () => toAdd.reduce((sum, c) => sum + quote(c, qty[c.id] || 1).total, 0),
    [toAdd, qty]
  );
  const ticketCount = toAdd.reduce((n, c) => n + (qty[c.id] || 1), 0);

  const submit = async () => {
    const doneIds = new Set(addedSoFar.map(({ c }) => c.id));
    const pending = competitions.filter((c) => !doneIds.has(c.id));
    const missing = {};
    pending.forEach((c) => {
      if (!answers[c.id]) missing[c.id] = 'Please answer the question.';
    });
    setErrors(missing);
    if (Object.keys(missing).length) return;

    setBusy(true);
    const added = [...addedSoFar];
    const failed = {};
    for (const c of pending) {
      try {
        const res = await fetch('/api/entry/lucky-dip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ competitionId: c.id, quantity: qty[c.id] || 1, answer: answers[c.id] }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          added.push({ c, count: data.added, asked: qty[c.id] || 1 });
          trackAddToCart(c, data.added);
        } else {
          failed[c.id] = data.error || 'Couldn’t add these tickets. Please try again.';
        }
      } catch {
        failed[c.id] = 'Couldn’t add these tickets. Please try again.';
      }
    }
    setBusy(false);
    setErrors(failed);
    setAddedSoFar(added);
    if (added.length > addedSoFar.length) {
      window.dispatchEvent(new CustomEvent('cart:changed'));
    }
    if (added.length && !Object.keys(failed).length) setDone(added);
  };

  const nextPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const rememberEntry = () => {
    if (!multi) {
      const c = competitions[0];
      savePendingEntry({
        competitionId: c.id,
        title: c.title,
        url: `/competitions/${c.category || 'products'}/${c.slug}`,
        mode: 'lucky-dip',
        quantity: qty[c.id] || 1,
      });
    }
  };
  const loginNext = encodeURIComponent(
    multi ? nextPath : `/competitions/${competitions[0].category || 'products'}/${competitions[0].slug}`
  );

  const remainingSuggestions = suggestions.filter((s) => !competitions.some((c) => c.id === s.id)).slice(0, 2);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="presentation">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-entry-title"
        className="relative z-[1] flex max-h-[92svh] w-full max-w-xl flex-col bg-[#101511] text-foreground outline-none sm:rounded-[8px] rounded-t-[12px] border border-white/10"
      >
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
          <h2 id="quick-entry-title" className="d text-[1.35rem]">
            {done ? 'In your basket.' : title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded text-3xl leading-none text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          {done ? (
            <div>
              <ul className="space-y-2">
                {done.map(({ c, count, asked }) => (
                  <li key={c.id} className="flex justify-between gap-4">
                    <span className="font-semibold">{c.title}</span>
                    <span className="shrink-0 tabular-nums text-foreground/85">
                      {count} ticket{count === 1 ? '' : 's'}
                      {count < asked ? ` (only ${count} left)` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm text-muted-foreground">
                Held for you for 15 minutes. Check out from your basket.
              </p>
              {remainingSuggestions.length > 0 && (
                <div className="mt-6 border-t border-white/10 pt-5">
                  <p className="text-sm font-semibold">Also drawn the same night</p>
                  <ul className="mt-2 space-y-2">
                    {remainingSuggestions.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-3">
                        <span className="text-sm">
                          {s.title} <span className="text-muted-foreground">· {price(s.ticketPrice)} a ticket</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => onSwitch?.(s)}
                          className="shrink-0 text-sm font-semibold text-accent-bright underline underline-offset-4"
                        >
                          Enter too
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            competitions.map((c) => {
              const already = addedSoFar.find((a) => a.c.id === c.id);
              return already ? (
                <p
                  key={c.id}
                  className={multi ? 'py-5 border-t border-white/10 first:border-t-0 first:pt-0' : ''}
                  role="status"
                >
                  <span className="font-semibold">{c.title}</span>: {already.count} ticket
                  {already.count === 1 ? '' : 's'} added to your basket.
                </p>
              ) : (
                <Entry
                  key={c.id}
                  c={c}
                  compact={multi}
                  qty={qty[c.id] || 1}
                  setQty={(n) => setQty((prev) => ({ ...prev, [c.id]: n }))}
                  answer={answers[c.id]}
                  setAnswer={(v) => {
                    setAnswers((prev) => ({ ...prev, [c.id]: v }));
                    setErrors((prev) => ({ ...prev, [c.id]: undefined }));
                  }}
                  error={errors[c.id]}
                />
              );
            })
          )}
        </div>

        <div className="border-t border-white/10 px-5 py-4">
          {done ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link href="/shopping-cart" className="btn-block btn-go">
                Go to basket <Arrow />
              </Link>
              <button type="button" onClick={onClose} className="link-more font-semibold">
                Keep browsing
              </button>
            </div>
          ) : !loading && !isAuthenticated ? (
            <div>
              <p className="text-sm text-muted-foreground">
                Create a free account or log in to add your tickets. We&apos;ll bring you straight back.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link href={`/register?next=${loginNext}`} onClick={rememberEntry} className="btn-block btn-go">
                  Create account
                </Link>
                <Link href={`/customer-login?next=${loginNext}`} onClick={rememberEntry} className="btn-block btn-line">
                  Log in
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm">
                <span className="font-semibold">{ticketCount} ticket{ticketCount === 1 ? '' : 's'}</span>
                <span className="block num text-[1.5rem] text-accent-bright">{fmt(total)}</span>
              </p>
              <button type="button" onClick={submit} disabled={busy} className="btn-block btn-go disabled:opacity-70">
                {busy ? 'Adding…' : 'Add to basket'} {!busy && <Arrow />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

QuickEntrySheet.propTypes = {
  competitions: PropTypes.array.isRequired,
  suggestions: PropTypes.array,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSwitch: PropTypes.func,
};
